/**
 * Step 4 - Service picker.
 *
 * Uses the full branch category tree:
 *   GET /api/{locale}/categories/tree/{branch}
 *
 * The UI mirrors the beauty demo catalog: category chips, search and
 * collapsible groups. Leaf services are selected directly and advance to
 * the time step.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchCategories,
  getServiceCategoryId,
  getServiceMeta,
  getServicePrice,
  pickTranslation,
} from "../../helper/bookingApi";
import {
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  SkeletonRow,
  StepCard,
  StepFooter,
  StepHeader,
  STEP_PAD,
} from "./BookingPrimitives";

function formatServicePrice(value) {
  return `${Number(value || 0).toLocaleString("hy-AM")} ֏`;
}

function getChildren(node) {
  const candidates = [
    node?.children,
    node?.categories,
    node?.sub_categories,
    node?.subCategories,
    node?.services,
    node?.items,
  ];
  return candidates.find((item) => Array.isArray(item)) || [];
}

function getNodeType(node) {
  const type = getServiceMeta(node)?.type ?? node?.type;
  const numeric = Number(type);
  return Number.isFinite(numeric) ? numeric : null;
}

function isGroupCategory(category) {
  const type = getNodeType(category);
  if (type === 0) return true;
  if (type === 1) return false;
  return getChildren(category).length > 0;
}

function getServiceDescription(item, lang) {
  if (item?.translate?.description) return item.translate.description;
  if (Array.isArray(item?.translates)) {
    const found = item.translates.find((tr) => tr.description);
    if (found?.description) return found.description;
  }
  return item?.description || item?.category?.description || "";
}

function getDuration(item, lang) {
  const raw =
    item?.average_waiting ??
    item?.duration ??
    item?.waiting_time ??
    item?.category?.average_waiting;
  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  if (minutes < 60) {
    return lang === "en" ? `${minutes} min` : `${minutes} ր`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return lang === "en" ? `${hours} h` : `${hours} ժ`;
  return lang === "en" ? `${hours} h ${rest} min` : `${hours} ժ ${rest} ր`;
}

function countWorkers(item, t) {
  const windows = getServiceMeta(item)?.windows || [];
  const count =
    item?.workers_count ??
    item?.masters_count ??
    item?.employees_count ??
    (Array.isArray(item?.workers) ? item.workers.length : null) ??
    windows.length;
  const numeric = Number(count);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return t("booking.service.workersCount", { count: numeric });
}

function HorizontalScrollArea({ palette, children, ariaLabel }) {
  const ref = useRef(null);
  const timerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    const max = node.scrollWidth - node.clientWidth;
    setCanLeft(node.scrollLeft > 4);
    setCanRight(node.scrollLeft < max - 4);
  }, []);

  useEffect(() => {
    update();
    const node = ref.current;
    if (!node) return undefined;
    node.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      node.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [children, update]);

  const scroll = (direction, amount = 220) => {
    const node = ref.current;
    if (!node) return;
    node.scrollBy({ left: direction * amount, behavior: "smooth" });
    window.setTimeout(update, 240);
  };

  const startHover = (direction) => {
    scroll(direction, 120);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => scroll(direction, 90), 140);
  };

  const stopHover = () => {
    if (!timerRef.current) return;
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const arrowStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: 34,
    height: 34,
    borderRadius: "50%",
    border: palette.border,
    background: palette.surface,
    color: palette.accent,
    boxShadow: palette.shadowSoft,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
  };

  return (
    <div style={{ position: "relative" }}>
      <style>
        {`
          .booking-scroll-x {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .booking-scroll-x::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {canLeft ? (
        <button
          type="button"
          aria-label={`${ariaLabel}: left`}
          onClick={() => scroll(-1)}
          onMouseEnter={() => startHover(-1)}
          onMouseLeave={stopHover}
          style={{ ...arrowStyle, left: 0 }}
        >
          <i className="las la-angle-left" aria-hidden="true" />
        </button>
      ) : null}
      <div
        ref={ref}
        className="booking-scroll-x"
        style={{
          display: "flex",
          gap: 10,
          overflowX: "auto",
          padding: "2px 2px 8px",
          scrollSnapType: "x mandatory",
        }}
      >
        {children}
      </div>
      {canRight ? (
        <button
          type="button"
          aria-label={`${ariaLabel}: right`}
          onClick={() => scroll(1)}
          onMouseEnter={() => startHover(1)}
          onMouseLeave={stopHover}
          style={{ ...arrowStyle, right: 0 }}
        >
          <i className="las la-angle-right" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

function ServiceRow({ palette, item, lang, selected, onSelect }) {
  const { t } = useTranslation();
  const name = pickTranslation(item, lang);
  const description = getServiceDescription(item, lang);
  const price = getServicePrice(item);
  const duration = getDuration(item, lang);
  const workers = countWorkers(item, t);

  return (
    <button
      type="button"
      onClick={onSelect}
      className="text-start w-100"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFamily: BOOKING_FONT_FAMILY,
        borderRadius: 14,
        border: selected ? `2px solid ${palette.accent}` : palette.border,
        background: selected ? palette.accentSoft : palette.surface,
        color: palette.text,
        padding: "clamp(12px, 3vw, 16px) clamp(12px, 3vw, 18px)",
        cursor: "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="d-block"
          style={{
            fontFamily: BOOKING_FONT_FAMILY,
            fontWeight: 700,
            color: palette.text,
            fontSize: "clamp(0.95rem, 2.4vw, 1.05rem)",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {name}
        </span>
        {description ? (
          <span
            className="d-block"
            style={{
              color: palette.textMuted,
              fontSize: "0.85rem",
              lineHeight: 1.35,
              marginTop: 4,
              wordBreak: "break-word",
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {description}
          </span>
        ) : null}
        <span
          className="d-flex flex-wrap align-items-center gap-2"
          style={{ marginTop: 8 }}
        >
          <span
            style={{
              background: palette.successSoft,
              color: palette.success,
              borderRadius: 999,
              padding: "3px 8px",
              fontSize: "0.72rem",
              fontWeight: 700,
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {t("booking.service.leafBadge")}
          </span>
          {price != 0 ? (

          <span
            style={{
              color: palette.text,
              fontWeight: 700,
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {formatServicePrice(price)}
          </span>
          ) : null}
          {duration ? (
            <span
              style={{
                color: palette.textMuted,
                fontSize: "0.85rem",
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              <i className="las la-clock me-1" aria-hidden="true" />
              {duration}
            </span>
          ) : null}
          {workers ? (
            <span
              style={{
                color: palette.textMuted,
                fontSize: "0.85rem",
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              <i className="las la-user-check me-1" aria-hidden="true" />
              {workers}
            </span>
          ) : null}
        </span>
      </span>
      <span
        style={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          background: selected ? palette.accent : "transparent",
          color: selected ? "#fff" : palette.accent,
          border: selected ? `1px solid ${palette.accent}` : `1.5px solid ${palette.accent}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: "1.25rem",
        }}
        aria-hidden="true"
      >
        <i className={selected ? "las la-check" : "las la-plus"} />
      </span>
    </button>
  );
}

function CategorySection({
  palette,
  category,
  lang,
  childrenNodes,
  openByDefault,
  selectedService,
  onSelect,
  depth = 0,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(Boolean(openByDefault));
  const selectedCategoryId =
    selectedService?.categoryId ?? getServiceCategoryId(selectedService);
  const leafCount = countLeafServices(childrenNodes);
  const selectedCount = countSelectedServices(childrenNodes, selectedCategoryId);

  useEffect(() => {
    setOpen(Boolean(openByDefault));
  }, [openByDefault]);

  return (
    <section
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-100 text-start d-flex align-items-center gap-3"
        style={{
          background: "transparent",
          border: "none",
          padding: "clamp(14px, 3vw, 18px)",
          color: palette.text,
          cursor: "pointer",
          fontFamily: BOOKING_FONT_FAMILY,
        }}
        aria-expanded={open}
      >
        <span
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: palette.accentSoft,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.3rem",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <i className="las la-layer-group" />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            className="d-block"
            style={{
              color: palette.text,
              fontFamily: BOOKING_FONT_FAMILY,
              fontWeight: 700,
              fontSize: depth > 0 ? "clamp(0.95rem, 2.4vw, 1.06rem)" : "clamp(1rem, 2.6vw, 1.15rem)",
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {pickTranslation(category, lang)}
          </span>
          <span
            style={{
              color: palette.textMuted,
              fontSize: "0.85rem",
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {getServiceDescription(category, lang) ||
              t("booking.service.servicesCount", { count: leafCount })}
            {selectedCount ? (
              <span
                style={{
                  color: palette.accent,
                  fontWeight: 700,
                  fontFamily: BOOKING_FONT_FAMILY,
                }}
              >
                {" · "}
                {t("booking.common.selected")}
              </span>
            ) : null}
          </span>
        </span>
        <i
          className="las la-angle-down"
          style={{
            color: palette.textMuted,
            fontSize: "1.2rem",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          style={{
            display: "grid",
            gap: 8,
            padding: "0 clamp(10px, 2.5vw, 14px) clamp(12px, 2.5vw, 16px)",
          }}
        >
          {childrenNodes.map((item) => (
            <ServiceNode
              key={`${item.id}-${getServiceCategoryId(item) || "group"}`}
              palette={palette}
              item={item}
              lang={lang}
              selectedService={selectedService}
              onSelect={onSelect}
              openByDefault={
                Boolean(openByDefault) ||
                containsSelectedService(getChildren(item), selectedCategoryId)
              }
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function countLeafServices(nodes) {
  return nodes.reduce((sum, item) => {
    if (!isGroupCategory(item)) return sum + 1;
    return sum + countLeafServices(getChildren(item));
  }, 0);
}

function countSelectedServices(nodes, selectedCategoryId) {
  if (selectedCategoryId == null) return 0;
  return nodes.reduce((sum, item) => {
    if (!isGroupCategory(item)) {
      return sum + (getServiceCategoryId(item) === selectedCategoryId ? 1 : 0);
    }
    return sum + countSelectedServices(getChildren(item), selectedCategoryId);
  }, 0);
}

function containsSelectedService(nodes, selectedCategoryId) {
  return countSelectedServices(nodes, selectedCategoryId) > 0;
}

function matchesSearch(item, lang, query) {
  if (!query) return true;
  const name = pickTranslation(item, lang).toLowerCase();
  const description = getServiceDescription(item, lang).toLowerCase();
  return name.includes(query) || description.includes(query);
}

function filterTree(nodes, lang, search) {
  const q = search.trim().toLowerCase();
  if (!q) return nodes;
  return nodes
    .map((item) => {
      if (!isGroupCategory(item)) return matchesSearch(item, lang, q) ? item : null;
      const children = filterTree(getChildren(item), lang, search);
      if (children.length > 0 || matchesSearch(item, lang, q)) {
        return { ...item, children };
      }
      return null;
    })
    .filter(Boolean);
}

function ServiceNode({
  palette,
  item,
  lang,
  selectedService,
  onSelect,
  openByDefault,
  depth = 0,
}) {
  if (isGroupCategory(item)) {
    return (
      <CategorySection
        palette={palette}
        category={item}
        childrenNodes={getChildren(item)}
        lang={lang}
        selectedService={selectedService}
        openByDefault={openByDefault}
        onSelect={onSelect}
        depth={depth}
      />
    );
  }

  const categoryId = getServiceCategoryId(item);
  const selectedCategoryId =
    selectedService?.categoryId ?? getServiceCategoryId(selectedService);
  const selected = selectedCategoryId != null && selectedCategoryId === categoryId;

  return (
    <ServiceRow
      palette={palette}
      item={item}
      lang={lang}
      selected={selected}
      onSelect={() => onSelect(item)}
    />
  );
}

export function ServiceStep({
  palette,
  lang,
  branch,
  selectedService,
  loadCategories = fetchCategories,
  autoAdvance = true,
  nextLabel,
  onSelect,
  onBack,
  onNext,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const branchId = branch?.id;

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await loadCategories(lang, { branchId });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[booking] services load failed", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [branchId, lang, loadCategories]);

  useEffect(() => {
    load();
  }, [load]);

  const categoryFilters = useMemo(
    () =>
      items
        .filter((item) => isGroupCategory(item))
        .map((item) => ({
          id: item.id,
          label: pickTranslation(item, lang),
        })),
    [items, lang],
  );

  const visibleNodes = useMemo(() => {
    const scopedItems =
      activeCategory === "all"
        ? items
        : items.filter((item) => String(item.id) === String(activeCategory));
    return filterTree(scopedItems, lang, search);
  }, [activeCategory, items, lang, search]);

  const handleSelect = (category) => {
    const categoryId = getServiceCategoryId(category);
    onSelect({ ...category, categoryId });
    if (autoAdvance && typeof onNext === "function") onNext();
  };

  return (
    <StepCard palette={palette} style={{ fontFamily: BOOKING_FONT_FAMILY }}>
      <StepHeader
        palette={palette}
        title={t("booking.service.title")}
        subtitle={t("booking.service.subtitle")}
      />

      <div style={{ padding: `4px ${STEP_PAD.x} ${STEP_PAD.contentBottom}` }}>
        {loading ? (
          <SkeletonRow palette={palette} count={5} />
        ) : error ? (
          <EmptyState
            palette={palette}
            icon="las la-exclamation-circle"
            title={t("booking.errors.loadServices")}
            action={<RetryButton palette={palette} onClick={load} />}
          />
        ) : items.length === 0 ? (
          <EmptyState
            palette={palette}
            icon="las la-folder-open"
            title={t("booking.service.emptyChildren")}
          />
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            <HorizontalScrollArea
              palette={palette}
              ariaLabel={t("booking.service.title")}
            >
              {[
                { id: "all", label: t("booking.service.all") },
                ...categoryFilters,
              ].map((category) => {
                const selected = String(activeCategory) === String(category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    style={{
                      fontFamily: BOOKING_FONT_FAMILY,
                      fontWeight: 700,
                      flexShrink: 0,
                      border: selected ? `1.5px solid ${palette.accent}` : palette.border,
                      background: selected ? palette.accent : palette.surface,
                      color: selected ? "#fff" : palette.text,
                      borderRadius: 999,
                      padding: "9px 13px",
                      fontSize: "0.86rem",
                      cursor: "pointer",
                      scrollSnapAlign: "start",
                    }}
                  >
                    <i
                      className={`${category.id === "all" ? "las la-border-all" : "las la-layer-group"} me-1`}
                      aria-hidden="true"
                    />
                    {category.label}
                  </button>
                );
              })}
            </HorizontalScrollArea>

            <div
              className="d-flex align-items-center"
              style={{
                background: palette.surface,
                border: palette.border,
                borderRadius: 14,
                padding: "10px 16px",
              }}
            >
              <i
                className="las la-search"
                style={{ color: palette.textMuted, fontSize: "1.15rem", marginRight: 10 }}
                aria-hidden="true"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("booking.service.searchPlaceholder")}
                style={{
                  fontFamily: BOOKING_FONT_FAMILY,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: palette.text,
                  flex: 1,
                  fontSize: "0.95rem",
                }}
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: palette.textMuted,
                    fontSize: "1.1rem",
                  }}
                >
                  <i className="las la-times" aria-hidden="true" />
                </button>
              ) : null}
            </div>

            {visibleNodes.length === 0 ? (
              <EmptyState
                palette={palette}
                icon="las la-search"
                title={t("booking.common.noResults")}
              />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {visibleNodes.map((item) => (
                  <ServiceNode
                    key={`${item.id}-${getServiceCategoryId(item) || "group"}`}
                    palette={palette}
                    item={item}
                    lang={lang}
                    selectedService={selectedService}
                    openByDefault={
                      Boolean(search) ||
                      containsSelectedService(
                        isGroupCategory(item) ? getChildren(item) : [item],
                        selectedService?.categoryId ??
                          getServiceCategoryId(selectedService),
                      )
                    }
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <StepFooter palette={palette}>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-500"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 18px",
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!selectedService}
          className="pill fw-500"
          style={{
            background: palette.accent,
            color: "#fff",
            border: "none",
            padding: "10px 24px",
            opacity: selectedService ? 1 : 0.5,
          }}
        >
          {nextLabel || t("booking.common.next")}
          <i className="las la-arrow-right ms-2" aria-hidden="true" />
        </button>
      </StepFooter>
    </StepCard>
  );
}
